<template>
  <div
    class="toggle-wrapper"
    data-pendo-ds="everyday_toggle"
  >
    <InputLabel
      v-if="label && size === 'regular'"
      class="toggle__label"
      :info="info"
      :label="label"
      :required="required"
    />  
    <label
      :aria-checked="value.toString()"
      :aria-label="ariaLabel || label"
      :aria-labelledby="ariaLabelledby"
      :class="[
        'toggle-switch',
        sizeClass,
        { 'toggle-switch--disabled': disabled },
        { 'toggle-switch--on': value },
        { 'toggle-switch--off': !value }
      ]"
      role="switch"
      :tabindex="disabled ? -1 : 0"
      @keydown.enter.prevent="handleKeydown"
      @keydown.space.prevent="handleKeydown"
    >
      <input
        ref="toggleInput"
        :checked="value"
        :disabled="disabled"
        type="checkbox"
        @blur="focus = false"
        @change="toggle"
        @focus="focus = true"
      >
      <span class="toggle-switch__slider" />
    </label>
    <span
      v-if="actiontext"
      class="toggle-switch__action-text"
      :class="{
        'toggle-switch__action-text--disabled': disabled,
        'label-s-regular': size === 'small',
        'label-l-regular': size === 'regular',
      }"
    >{{ actiontext }}</span>
    <div class="toggle-errors">
      <slot name="errors" />
    </div>
  </div>
</template>

<script>
import InputLabel from './InputLabel.vue'
const SIZES = ['small', 'regular']

export default {
  name: 'EverydayToggle',
  components: {
    InputLabel,
  },
  props: {
    value: {
      type: Boolean,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: 'regular',
      validator: (value) => SIZES.includes(value),
    },
    actiontext: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    ariaLabel: {
      type: String,
      default: '',
    },
    ariaLabelledby: {
      type: String,
      default: '',
    },
    info: {
      type: String,
      default: '',
    },
    required: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:value'],
  computed: {
    sizeClass() {
      return this.size === 'small' ? 'toggle-switch--small' : 'toggle-switch--regular'
    },
  },
  methods: {
    toggle(event) {
      if (!this.disabled) {
        this.$emit('update:value', event.target.checked)
      }
    },
    handleKeydown() {
      if (!this.disabled) {
        this.$emit('update:value', !this.value)
      }
    },
  },
}
</script>

<style scoped>
.toggle-wrapper {
  display: inline-grid;
  align-items: center;
  grid-template-areas: 
    "label label"
    "toggle action"
    "errors errors";
  column-gap: var(--space-2);
  row-gap: var(--space-1);
}

.toggle__label {
  grid-area: label;
}

.toggle-switch {
  display: flex;
  align-items: center;
  cursor: pointer;
  position: relative;
  grid-area: toggle;
  outline: none;
  border-radius: 50rem;
  &:focus-visible {
    box-shadow: 0 0 0 2px var(--blueberry-300);
  }
}

.toggle-switch__action-text {
  grid-area: action;
  color: var(--pepper-1000)
}

.toggle-switch input {
  display: none;
}

.toggle-switch--disabled {
  cursor: not-allowed;
}

.toggle-switch--small {
  width: 32px;
  height: 16px;
}

.toggle-switch--regular {
  width: 48px;
  height: 24px;
}

.toggle-switch__slider {
  width: 100%;
  height: 100%;
  border-radius: 9999px;
  position: relative;
  transition: background-color 0.3s, box-shadow 0.3s;
}

.toggle-switch__slider::before {
  content: "";
  position: absolute;
  top: 50%;
  left: 2px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background-color: white;
  transition: transform 0.3s;
  transform: translateY(-50%);
}

.toggle-switch--small .toggle-switch__slider::before {
  width: 12px;
  height: 12px;
}

.toggle-switch--regular .toggle-switch__slider::before {
  width: 20px;
  height: 20px;
}

.toggle-switch--on .toggle-switch__slider::before {
  transform: translate(24px, -50%);
}

.toggle-switch--small.toggle-switch--on .toggle-switch__slider::before {
  transform: translate(16px, -50%);
}

.toggle-switch--regular.toggle-switch--on .toggle-switch__slider::before {
  transform: translate(24px, -50%);
}

.toggle-switch--on .toggle-switch__slider {
  background-color: var(--blueberry-1000);
}

.toggle-switch--off .toggle-switch__slider {
  background-color: var(--pepper-600);
}

.toggle-switch input:focus + .toggle-switch__slider {
  box-shadow: 0 0 0 2px var(--blueberry-300);
}

.toggle-switch input:not(:disabled):hover + .toggle-switch__slider {
  background-color: var(--pepper-500);
}

.toggle-switch--on input:not(:disabled):hover + .toggle-switch__slider {
  background-color: var(--blueberry-800);
}

.toggle-switch--disabled .toggle-switch__slider {
  background-color: var(--pepper-300);
}

.toggle-switch--on.toggle-switch--disabled .toggle-switch__slider {
  background-color: var(--blueberry-300);
}

.toggle__label--disabled, .toggle-switch__action-text--disabled {
  color: var(--font-color-disabled);
}

.toggle-errors {
  grid-area: errors;
}
</style>
